import React, { Component, ErrorInfo, ReactNode } from 'react';
import { hardReload } from '../utils/hardReload';
import '../styles/components/errorBoundary.css';

interface Props {
  children: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
  retryCount: number;
  isRetrying: boolean;
  countdown: number;
}

class ErrorBoundary extends Component<Props, State> {
  private retryTimer: NodeJS.Timeout | null = null;
  private countdownTimer: NodeJS.Timeout | null = null;
  private readonly MAX_RETRIES = 5;
  private readonly RETRY_DELAYS = [5, 10, 15, 20, 30]; // seconds

  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
      retryCount: 0,
      isRetrying: false,
      countdown: 0,
    };
  }

  static getDerivedStateFromError(error: Error): Partial<State> {
    return {
      hasError: true,
      error,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo) {
    console.error('ErrorBoundary caught an error:', error, errorInfo);
    
    this.setState({
      error,
      errorInfo,
    });

    // Start auto-retry after first error
    this.scheduleRetry();
  }

  componentWillUnmount() {
    this.clearTimers();
  }

  clearTimers = () => {
    if (this.retryTimer) {
      clearTimeout(this.retryTimer);
      this.retryTimer = null;
    }
    if (this.countdownTimer) {
      clearInterval(this.countdownTimer);
      this.countdownTimer = null;
    }
  };

  scheduleRetry = () => {
    const { retryCount } = this.state;
    
    if (retryCount >= this.MAX_RETRIES) {
      console.log('Max retries reached');
      return;
    }

    const delaySeconds = this.RETRY_DELAYS[retryCount] || 30;
    
    this.setState({
      isRetrying: true,
      countdown: delaySeconds,
    });

    // Start countdown
    this.countdownTimer = setInterval(() => {
      this.setState((prevState) => {
        const newCountdown = prevState.countdown - 1;
        if (newCountdown <= 0) {
          if (this.countdownTimer) {
            clearInterval(this.countdownTimer);
            this.countdownTimer = null;
          }
        }
        return { countdown: newCountdown };
      });
    }, 1000);

    // Schedule retry
    this.retryTimer = setTimeout(() => {
      this.handleRetry();
    }, delaySeconds * 1000);
  };

  handleRetry = () => {
    this.clearTimers();
    
    const { retryCount } = this.state;
    
    console.log(`Retry attempt ${retryCount + 1} of ${this.MAX_RETRIES}`);
    
    // Check if app is available by trying to reload
    this.setState({
      isRetrying: true,
      retryCount: retryCount + 1,
    });

    // Cache-busting reload to get fresh index.html after deployment
    hardReload();
  };

  handleManualRetry = () => {
    this.clearTimers();
    console.log('Manual retry triggered');
    hardReload();
  };

  render() {
    const { hasError, error, retryCount, isRetrying, countdown } = this.state;

    if (hasError) {
      const isMaxRetries = retryCount >= this.MAX_RETRIES;

      return (
        <div className="error-boundary">
          <div className="error-boundary-content">
            <div className="error-boundary-icon">
              <svg width="64" height="64" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                <circle cx="12" cy="12" r="10" />
                <line x1="12" y1="8" x2="12" y2="12" />
                <line x1="12" y1="16" x2="12.01" y2="16" />
              </svg>
            </div>

            <h1 className="error-boundary-title">
              {isMaxRetries ? 'Unable to Connect' : 'Server Temporarily Unavailable'}
            </h1>

            <p className="error-boundary-message">
              {isMaxRetries ? (
                <>
                  We've tried reconnecting several times but couldn't reach the server.
                  <br />
                  Please check your internet connection and try again.
                </>
              ) : (
                <>
                  The app is updating or temporarily unavailable.
                  <br />
                  {isRetrying && countdown > 0 ? (
                    <>Retrying in {countdown} second{countdown !== 1 ? 's' : ''}...</>
                  ) : (
                    <>Attempting to reconnect...</>
                  )}
                </>
              )}
            </p>

            {!isMaxRetries && (
              <div className="error-boundary-progress">
                <div className="error-boundary-progress-text">
                  Attempt {retryCount} of {this.MAX_RETRIES}
                </div>
                <div className="error-boundary-progress-bar">
                  <div 
                    className="error-boundary-progress-fill"
                    style={{ width: `${(retryCount / this.MAX_RETRIES) * 100}%` }}
                  />
                </div>
              </div>
            )}

            <button
              className="error-boundary-retry-button"
              onClick={this.handleManualRetry}
            >
              {isMaxRetries ? 'Try Again' : 'Retry Now'}
            </button>

            {process.env.NODE_ENV === 'development' && error && (
              <details className="error-boundary-details">
                <summary>Error Details (Development Only)</summary>
                <pre className="error-boundary-stack">
                  {error.toString()}
                  {this.state.errorInfo?.componentStack}
                </pre>
              </details>
            )}
          </div>
        </div>
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;
